import React, { useMemo, useState } from 'react'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Download,
  Search,
  ChevronsDownUp,
  ChevronsUpDown,
} from 'lucide-react'
import { Button } from '../ui/Button'
import type { RfidMatrixGroup, RfidMatrixRow } from '../../types/rfid'
import type {
  UpsertColorIdentifierInput,
  UpsertItemIdentifierInput,
  UpsertSizeIdentifierInput,
} from '../../types/rfid'
import { exportRfidIdentifiersToExcel } from '../../utils/rfidExport'

type SortField = 'itemName' | 'sku' | 'barcode' | 'itemCode' | 'colorName' | 'sizeName'
type IdentifierFilter = 'all' | 'missing' | 'duplicates'

interface RfidIdentifierMatrixProps {
  groups: RfidMatrixGroup[]
  canEdit: boolean
  isSaving: boolean
  duplicateItemCodes: Set<string>
  duplicateColorCodes: Set<string>
  duplicateSizeCodes: Set<string>
  selectedProductId: string | null
  onSelectRow: (row: RfidMatrixRow) => void
  onSaveItem: (input: UpsertItemIdentifierInput) => Promise<unknown>
  onSaveColor: (input: UpsertColorIdentifierInput) => Promise<unknown>
  onSaveSize: (input: UpsertSizeIdentifierInput) => Promise<unknown>
}

function InlineCodeInput({
  value,
  placeholder,
  disabled,
  hasError,
  onSave,
}: {
  value: string
  placeholder: string
  disabled: boolean
  hasError?: boolean
  onSave: (next: string) => Promise<void>
}) {
  const [draft, setDraft] = useState(value)
  const [isBusy, setIsBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  React.useEffect(() => {
    setDraft(value)
  }, [value])

  async function commit() {
    const next = draft.trim()
    if (next === (value || '').trim()) return
    setIsBusy(true)
    setLocalError(null)
    try {
      await onSave(next)
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Save failed')
      setDraft(value)
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <div className="min-w-[96px]">
      <input
        value={draft}
        disabled={disabled || isBusy}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void commit()
          }
          if (e.key === 'Escape') {
            setDraft(value)
            setLocalError(null)
          }
        }}
        className={`w-full h-9 rounded-md border px-2.5 font-mono text-sm ${
          hasError || localError
            ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
        } disabled:opacity-60`}
      />
      {localError && (
        <div className="text-[11px] text-red-600 mt-0.5 leading-tight">{localError}</div>
      )}
    </div>
  )
}

function CodeChip({ value, missing }: { value: string | null; missing?: boolean }) {
  if (!value) {
    return (
      <span className="inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-2 py-1 text-xs font-medium">
        {missing === false ? '—' : 'Missing'}
      </span>
    )
  }
  return <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{value}</span>
}

export function RfidIdentifierMatrix({
  groups,
  canEdit,
  isSaving,
  duplicateItemCodes,
  duplicateColorCodes,
  duplicateSizeCodes,
  selectedProductId,
  onSelectRow,
  onSaveItem,
  onSaveColor,
  onSaveSize,
}: RfidIdentifierMatrixProps) {
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [identifierFilter, setIdentifierFilter] = useState<IdentifierFilter>('all')
  const [sortField, setSortField] = useState<SortField>('itemName')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const pageSize = 25

  const categories = useMemo(() => {
    const set = new Set<string>()
    for (const group of groups) {
      if (group.category) set.add(group.category)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [groups])

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase()

    let next = groups.filter((group) => {
      if (categoryFilter !== 'all' && group.category !== categoryFilter) return false

      const hasDuplicate = group.variants.some((row) => {
        const itemDup = row.itemCode
          ? duplicateItemCodes.has(row.itemCode.trim().toLowerCase())
          : false
        const colorDup = row.colorCode
          ? duplicateColorCodes.has(row.colorCode.trim().toLowerCase())
          : false
        const sizeDup = row.sizeCode
          ? duplicateSizeCodes.has(row.sizeCode.trim().toLowerCase())
          : false
        return itemDup || colorDup || sizeDup
      })

      if (identifierFilter === 'missing' && !group.hasMissingIdentifiers) return false
      if (identifierFilter === 'duplicates' && !hasDuplicate) return false

      if (!q) return true
      if (group.itemName.toLowerCase().includes(q)) return true
      if (group.category.toLowerCase().includes(q)) return true
      if (group.itemCode?.toLowerCase().includes(q)) return true
      if (group.styleCode?.toLowerCase().includes(q)) return true
      return group.variants.some(
        (row) =>
          row.sku.toLowerCase().includes(q) ||
          (row.barcode || '').toLowerCase().includes(q) ||
          row.colorName.toLowerCase().includes(q) ||
          row.sizeName.toLowerCase().includes(q) ||
          (row.colorCode || '').toLowerCase().includes(q) ||
          (row.sizeCode || '').toLowerCase().includes(q)
      )
    })

    next = [...next].sort((a, b) => {
      const av =
        sortField === 'itemName'
          ? a.itemName
          : sortField === 'itemCode'
            ? a.itemCode || ''
            : a.variants[0]?.[sortField] || ''
      const bv =
        sortField === 'itemName'
          ? b.itemName
          : sortField === 'itemCode'
            ? b.itemCode || ''
            : b.variants[0]?.[sortField] || ''
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true })
      return sortDir === 'asc' ? cmp : -cmp
    })

    return next
  }, [
    groups,
    search,
    categoryFilter,
    identifierFilter,
    sortField,
    sortDir,
    duplicateItemCodes,
    duplicateColorCodes,
    duplicateSizeCodes,
  ])

  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / pageSize))
  const pageGroups = filteredGroups.slice((page - 1) * pageSize, page * pageSize)

  const missingCount = groups.filter((g) => g.hasMissingIdentifiers).length
  const duplicateGroupCount = groups.filter((group) =>
    group.variants.some((row) => {
      const itemDup = row.itemCode
        ? duplicateItemCodes.has(row.itemCode.trim().toLowerCase())
        : false
      const colorDup = row.colorCode
        ? duplicateColorCodes.has(row.colorCode.trim().toLowerCase())
        : false
      const sizeDup = row.sizeCode
        ? duplicateSizeCodes.has(row.sizeCode.trim().toLowerCase())
        : false
      return itemDup || colorDup || sizeDup
    })
  ).length

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortField(field)
    setSortDir('asc')
  }

  function toggleExpanded(itemName: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(itemName)) next.delete(itemName)
      else next.add(itemName)
      return next
    })
  }

  function expandAll() {
    setExpanded(new Set(pageGroups.map((g) => g.itemName)))
  }

  function collapseAll() {
    setExpanded(new Set())
  }

  async function handleExport() {
    const rows = filteredGroups.flatMap((group) => group.variants)
    await exportRfidIdentifiersToExcel(rows)
  }

  const thClass =
    'px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 whitespace-nowrap bg-gray-50 dark:bg-gray-800/95'

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-5 pt-5 pb-4 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              RFID Identifier Matrix
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {groups.length} products · {groups.reduce((sum, g) => sum + g.variantCount, 0)} variants
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={expandAll}>
              <ChevronsUpDown className="h-4 w-4 mr-1" />
              Expand page
            </Button>
            <Button variant="secondary" size="sm" onClick={collapseAll}>
              <ChevronsDownUp className="h-4 w-4 mr-1" />
              Collapse
            </Button>
            <Button variant="secondary" size="sm" onClick={() => void handleExport()}>
              <Download className="h-4 w-4 mr-1" />
              Export Excel
            </Button>
          </div>
        </div>

        {(missingCount > 0 || duplicateGroupCount > 0) && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              {missingCount > 0 && (
                <div>{missingCount} product(s) have missing or invalid EPC fields.</div>
              )}
              {duplicateGroupCount > 0 && (
                <div>{duplicateGroupCount} product(s) include duplicated identifiers.</div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder="Search name, SKU, barcode, color, size…"
              className="w-full h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-10 pr-3 text-sm"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value)
              setPage(1)
            }}
            className="h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm min-w-[160px]"
          >
            <option value="all">All categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <select
            value={identifierFilter}
            onChange={(e) => {
              setIdentifierFilter(e.target.value as IdentifierFilter)
              setPage(1)
            }}
            className="h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm min-w-[180px]"
          >
            <option value="all">All identifiers</option>
            <option value="missing">Missing identifiers</option>
            <option value="duplicates">Duplicate identifiers</option>
          </select>
        </div>
      </div>

      <div className="overflow-auto max-h-[70vh] border-t border-gray-200 dark:border-gray-700">
        <table className="min-w-[1400px] w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className={`${thClass} sticky left-0 z-30 w-10`} />
              <th
                className={`${thClass} sticky left-10 z-30 min-w-[220px] cursor-pointer`}
                onClick={() => toggleSort('itemName')}
              >
                Item name
              </th>
              <th className={`${thClass} cursor-pointer`} onClick={() => toggleSort('itemCode')}>
                Item ID
              </th>
              <th className={thClass}>Style</th>
              <th className={thClass}>Year</th>
              <th className={thClass}>Season</th>
              <th className={`${thClass} cursor-pointer`} onClick={() => toggleSort('colorName')}>
                Color
              </th>
              <th className={thClass}>Color ID</th>
              <th className={`${thClass} cursor-pointer`} onClick={() => toggleSort('sizeName')}>
                Size
              </th>
              <th className={thClass}>Size ID</th>
              <th className={`${thClass} cursor-pointer`} onClick={() => toggleSort('sku')}>
                SKU
              </th>
              <th className={`${thClass} cursor-pointer`} onClick={() => toggleSort('barcode')}>
                Barcode
              </th>
              <th className={thClass}>EPC base</th>
            </tr>
          </thead>
          <tbody>
            {pageGroups.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-4 py-10 text-center text-gray-500">
                  No products match the current filters.
                </td>
              </tr>
            ) : (
              pageGroups.map((group) => {
                const isExpanded = expanded.has(group.itemName)
                const first = group.variants[0]
                return (
                  <React.Fragment key={group.itemName}>
                    <tr className="bg-slate-50 dark:bg-slate-800/40">
                      <td className="px-3 py-3 sticky left-0 z-10 bg-slate-50 dark:bg-slate-800/95 border-b border-gray-200 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(group.itemName)}
                          className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-3 sticky left-10 z-10 bg-slate-50 dark:bg-slate-800/95 border-b border-gray-200 dark:border-gray-700 min-w-[220px]">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {group.itemName}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {group.variantCount} variant{group.variantCount === 1 ? '' : 's'}
                          {group.category ? ` · ${group.category}` : ''}
                        </div>
                      </td>
                      <td className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
                        <InlineCodeInput
                          value={group.itemCode || ''}
                          placeholder="e.g. 05"
                          disabled={!canEdit || isSaving}
                          hasError={
                            !group.itemCode ||
                            (!!group.itemCode &&
                              duplicateItemCodes.has(group.itemCode.trim().toLowerCase()))
                          }
                          onSave={async (next) => {
                            await onSaveItem({
                              item_name: group.itemName,
                              item_code: next,
                              style_code: group.styleCode,
                              epc_year: group.epcYear,
                              epc_season: group.epcSeason,
                            })
                          }}
                        />
                      </td>
                      <td className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
                        <InlineCodeInput
                          value={group.styleCode || ''}
                          placeholder="e.g. 004821"
                          disabled={!canEdit || isSaving || !group.itemCode}
                          onSave={async (next) => {
                            if (!group.itemCode) throw new Error('Set item identifier first')
                            await onSaveItem({
                              item_name: group.itemName,
                              item_code: group.itemCode,
                              style_code: next,
                              epc_year: group.epcYear,
                              epc_season: group.epcSeason,
                            })
                          }}
                        />
                      </td>
                      <td className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
                        <InlineCodeInput
                          value={group.epcYear || ''}
                          placeholder="26"
                          disabled={!canEdit || isSaving || !group.itemCode}
                          onSave={async (next) => {
                            if (!group.itemCode) throw new Error('Set item identifier first')
                            await onSaveItem({
                              item_name: group.itemName,
                              item_code: group.itemCode,
                              style_code: group.styleCode,
                              epc_year: next,
                              epc_season: group.epcSeason,
                            })
                          }}
                        />
                      </td>
                      <td className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
                        <InlineCodeInput
                          value={group.epcSeason || ''}
                          placeholder="2"
                          disabled={!canEdit || isSaving || !group.itemCode}
                          onSave={async (next) => {
                            if (!group.itemCode) throw new Error('Set item identifier first')
                            await onSaveItem({
                              item_name: group.itemName,
                              item_code: group.itemCode,
                              style_code: group.styleCode,
                              epc_year: group.epcYear,
                              epc_season: next,
                            })
                          }}
                        />
                      </td>
                      <td
                        className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 text-gray-500"
                        colSpan={6}
                      >
                        {isExpanded
                          ? 'Variants below'
                          : first
                            ? `${first.colorName} / ${first.sizeName} · ${first.sku}`
                            : '—'}
                      </td>
                      <td className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 font-mono text-xs text-gray-500">
                        {first?.epcBase || '—'}
                      </td>
                    </tr>

                    {isExpanded &&
                      group.variants.map((row) => {
                        const isSelected = selectedProductId === row.productId
                        const colorDup = row.colorCode
                          ? duplicateColorCodes.has(row.colorCode.trim().toLowerCase())
                          : false
                        const sizeDup = row.sizeCode
                          ? duplicateSizeCodes.has(row.sizeCode.trim().toLowerCase())
                          : false

                        return (
                          <tr
                            key={row.productId}
                            onClick={() => onSelectRow(row)}
                            className={`cursor-pointer ${
                              isSelected
                                ? 'bg-teal-50 dark:bg-teal-900/25'
                                : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                            }`}
                          >
                            <td className="px-3 py-2.5 sticky left-0 z-10 bg-inherit border-b border-gray-100 dark:border-gray-800" />
                            <td className="px-3 py-2.5 sticky left-10 z-10 bg-inherit border-b border-gray-100 dark:border-gray-800 pl-8 text-xs text-gray-500">
                              variant
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
                              <CodeChip value={row.itemCode} />
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
                              <CodeChip value={row.styleCode} />
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
                              <CodeChip value={row.epcYear} />
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
                              <CodeChip value={row.epcSeason} />
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
                              {row.colorName}
                            </td>
                            <td
                              className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.colorId ? (
                                <InlineCodeInput
                                  value={row.colorCode || ''}
                                  placeholder="e.g. 010"
                                  disabled={!canEdit || isSaving}
                                  hasError={!row.colorCode || colorDup}
                                  onSave={async (next) => {
                                    await onSaveColor({ color_id: row.colorId!, code: next })
                                  }}
                                />
                              ) : (
                                <CodeChip value={null} missing={false} />
                              )}
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
                              {row.sizeName}
                            </td>
                            <td
                              className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.sizeId ? (
                                <InlineCodeInput
                                  value={row.sizeCode || ''}
                                  placeholder="e.g. 38"
                                  disabled={!canEdit || isSaving}
                                  hasError={!row.sizeCode || sizeDup}
                                  onSave={async (next) => {
                                    await onSaveSize({ size_id: row.sizeId!, code: next })
                                  }}
                                />
                              ) : (
                                <CodeChip value={null} missing={false} />
                              )}
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 font-mono text-sm">
                              {row.sku}
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 font-mono text-sm">
                              {row.barcode || '—'}
                            </td>
                            <td className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 font-mono text-xs">
                              {row.epcBase ? (
                                <span className="text-emerald-700 dark:text-emerald-300">{row.epcBase}</span>
                              ) : (
                                <span className="text-amber-700 dark:text-amber-300">
                                  {(row.missingSegments.length
                                    ? `Missing ${row.missingSegments.join(', ')}`
                                    : row.lengthIssues[0]) || 'Incomplete'}
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500">
        <div>
          Showing {pageGroups.length} of {filteredGroups.length} products
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span>
            Page {page} / {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
